"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { fadeInUp } from "@/lib/animations";
import {
  MapPin,
  Phone,
  Mail,
  Instagram,
  Twitter,
  Linkedin,
  Facebook,
} from "lucide-react";

const contactInfo = [
  {
    icon: MapPin,
    label: "Address",
    value: "King Abdullah Financial District, parcel 2.08 1st floor",
    link: "https://maps.app.goo.gl/cZk283aegX1mEBxV8?g_st=ic",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "+966 54 041 4979",
    link: "tel:+966540414979",
  },
  {
    icon: Mail,
    label: "Email",
    value: "office@idworkglobal.com",
    link: "mailto:office@idworkglobal.com",
  },
];

const socialLinks = [
  {
    icon: Instagram,
    href: "https://www.instagram.com/idworksglobal?igsh=dTV4cHNyNms1MHJu",
    label: "Instagram",
  },
  // { icon: Twitter, href: "#", label: "Twitter" },
  {
    icon: Linkedin,
    href: "https://www.linkedin.com/company/idworkglobal/",
    label: "LinkedIn",
  },
  // { icon: Facebook, href: "#", label: "Facebook" },
];

export default function Footer() {
  return (
    <footer className="bg-secondary text-white pt-20 pb-8 px-8">
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-16">
          {/* Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Image
              src="/images/IDWorksLogo.svg"
              alt="idworks Hub"
              width={200}
              height={100}
            />
            <p className="text-white/70 leading-relaxed mb-6">
              A premier creative destination in KAFD, Riyadh. Empowering Saudi
              designers through innovation, collaboration, and community.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social, idx) => (
                <motion.a
                  key={idx}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-primary flex items-center justify-center transition-all duration-300"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h3 className="text-xl font-bold mb-6">Contact Us</h3>
            <div className="space-y-4">
              {contactInfo.map((info, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <info.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm mb-1">{info.label}</p>
                    {info.link ? (
                      <a
                        href={info.link}
                        className="text-white hover:text-primary transition-colors"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-white">{info.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-xl font-bold mb-6">Location</h3>
            <a
              href="https://maps.app.goo.gl/cZk283aegX1mEBxV8?g_st=ic"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-2xl overflow-hidden h-48 bg-white/10 hover:opacity-90 transition-opacity"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14498.089088888889!2d46.6365219!3d24.7233160!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e2f0350e6b4c7a9%3A0x7f5e3b4c8e9f1a2b!2sIDWORKS%20HUB!5e0!3m2!1sen!2s!4v1734364800000!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0, pointerEvents: "none" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </a>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-white/50 text-sm">
            <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
              <p>© 2025 idworks Hub. All rights reserved.</p>
              <span className="hidden md:inline">•</span>
              <p>Powered by Bridge X</p>
            </div>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
